import { Component } from '@theme/component';
import { prefersReducedMotion, debounce, preloadImage } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /* THUMBNAILS FOLLOW THE PHOTO, 2026-10-10 — owner: "zoom mode mobile/desktop
     thumbnails không đổi theo main images scroll". On a phone the photos scroll in
     a box INSIDE the dialog, and scroll does not bubble, so a listener on the dialog
     never heard it: the selection sat on photo 1. Capture phase hears every scroller
     under the dialog; the thumbnail strip's own scrolling is left out. */
  #onScroll = (event) => {
    const { thumbnails } = this.refs;
    if (thumbnails && event.target instanceof Node && thumbnails.contains(event.target)) return;
    this.handleScroll();
  };

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.#onScroll, { capture: true, passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.#onScroll, { capture: true });
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  /* OPEN AND CLOSE WITHOUT A VIEW TRANSITION, 2026-10-15 — owner: "zoom mode còn
     giật giật", "zoom mode exit cần làm chuẩn clear animations mượt".

     Filmed frame by frame on a phone viewport (preview 161066287151): opening, the
     whole page washed to white while two copies of the photograph overlapped, and
     the rail arrived ~500ms later; closing, the rail slid away for 250ms with the
     photograph frozen, then the page popped back with no movement at all. With the
     CPU at phone speed the open held single frames for 400-500ms: a view
     transition snapshots the entire page, and the dialog's own opacity fade ran on
     top of it.

     Now one motion, transform and opacity only: the tapped photograph flies from
     where it sat on the page to fullscreen while the ground and the rail fade in
     with it, and closing runs the same flight back to the slide it lands on. The
     dialog is only closed once that flight has finished, so nothing pops. Web
     Animations rather than CSS keyframes also means the resting state is the
     visible one — session replay does not replay these, and the rail used to sit
     at the keyframes' opacity: 0 in every recording. */
  static #EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
  static #MS = 280;

  /** @type {Animation[]} */
  #running = [];
  #closing = false;
  #openedAt = 0;
  #lastIndex = -1;
  #seen = new Set();
  #thumbAt = 0;

  /**
   * The rectangle the bitmap actually occupies inside an <img> box. For
   * object-fit: contain that is smaller than the box, and the flight has to line
   * up the pictures, not the boxes.
   * @param {HTMLImageElement | null | undefined} img
   */
  static #paintedRect(img) {
    if (!img) return null;
    const r = img.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (getComputedStyle(img).objectFit !== 'contain' || !nw || !nh) return r;
    const s = Math.min(r.width / nw, r.height / nh);
    const w = nw * s;
    const h = nh * s;
    return new DOMRect(r.left + (r.width - w) / 2, r.top + (r.height - h) / 2, w, h);
  }

  /**
   * The transform (origin 0 0) that lays `item`'s picture, painted at `pic`, over `to`.
   * @param {HTMLElement} item @param {DOMRect} pic @param {DOMRect} to
   */
  static #flight(item, pic, to) {
    const box = item.getBoundingClientRect();
    const s = Math.max(to.width / pic.width, to.height / pic.height);
    const tx = to.left + (to.width - pic.width * s) / 2 - box.left - s * (pic.left - box.left);
    const ty = to.top + (to.height - pic.height * s) / 2 - box.top - s * (pic.top - box.top);
    return `translate(${tx}px, ${ty}px) scale(${s})`;
  }

  /** @param {DOMRect | null} r */
  static #onScreen(r) {
    return !!r && r.width > 20 && r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
  }

  #stopRunning() {
    for (const a of this.#running) a.cancel();
    this.#running = [];
  }

  /** What fades with the ground: the close button and the thumbnail rail. */
  #chrome() {
    const { dialog } = this.refs;
    return [
      dialog.querySelector('.dialog-zoomed-gallery__close-button'),
      dialog.querySelector('.dialog-thumbnails-list-container'),
    ].filter(Boolean);
  }

  /**
   * For analytics (hl-shop-tracking.js listens on document). The dialog knows
   * what happened inside it; nothing outside can tell a swipe from a tap.
   * @param {'open' | 'change' | 'close'} action
   * @param {Record<string, unknown>} detail
   */
  #report(action, detail) {
    this.dispatchEvent(new CustomEvent('hl:zoom', { bubbles: true, detail: { action, ...detail } }));
  }

  /** @param {number} index @param {'swipe' | 'thumbnail'} method */
  #reportChange(index, method) {
    if (!this.refs.dialog.open || index < 0 || index === this.#lastIndex) return;
    this.#lastIndex = index;
    this.#seen.add(index);
    this.#report('change', { index, method });
  }

  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    this.#stopRunning();
    this.#closing = false;
    this.#prepareForOpen(index, event);

    /** @type {HTMLElement | null} */
    const sourceSlide = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;
    const from = ZoomDialog.#paintedRect(sourceSlide?.querySelector('img'));

    this.#openedAt = Date.now();
    this.#lastIndex = index;
    this.#seen = new Set([index]);

    // The stylesheet fades the whole dialog in on [open]; that would fade the
    // photograph that is supposed to be flying. The ground fades instead.
    dialog.style.transition = 'none';
    dialog.showModal();
    for (const target of [targetThumbnail, targetImage]) {
      target?.scrollIntoView({ behavior: 'instant' });
    }
    /* Not selectThumbnail(): that also tells media-gallery to move the page's
       strip to `index` — where it already is, since that is the slide that was
       tapped — and the relayout of the page under the dialog landed in the same
       task as the tap. Marking the thumbnail is all that is needed here; the
       sharper file and the analytics wait until the photograph has landed. */
    for (const [i, button] of Array.from(thumbnails.querySelectorAll('button')).entries()) {
      button.setAttribute('aria-selected', `${i === index}`);
    }
    // Where the phone layout parks the × (just above the rail); see the stylesheet.
    const rail = dialog.querySelector('.dialog-thumbnails-list-container');
    if (rail) dialog.style.setProperty('--hl-zd-rail', `${Math.round(rail.getBoundingClientRect().height)}px`);

    const settle = () => {
      if (targetImage) this.loadHighResolutionImage(targetImage);
      this.#report('open', { index, count: media.length });
    };

    if (prefersReducedMotion()) {
      dialog.style.transition = '';
      settle();
      return;
    }

    const opts = { duration: ZoomDialog.#MS, easing: ZoomDialog.#EASE };
    const ground = getComputedStyle(dialog).backgroundColor;
    this.#running.push(dialog.animate([{ backgroundColor: 'transparent' }, { backgroundColor: ground }], opts));
    for (const el of this.#chrome()) {
      this.#running.push(
        el.animate([{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'none' }], {
          ...opts,
          delay: 80,
          fill: 'backwards',
        })
      );
    }

    const pic = ZoomDialog.#paintedRect(targetImage?.querySelector('img'));
    if (targetImage && pic && ZoomDialog.#onScreen(from)) {
      targetImage.style.transformOrigin = '0 0';
      this.#running.push(
        targetImage.animate([{ transform: ZoomDialog.#flight(targetImage, pic, from) }, { transform: 'none' }], opts)
      );
    } else if (targetImage) {
      this.#running.push(
        targetImage.animate([{ opacity: 0, transform: 'scale(0.96)' }, { opacity: 1, transform: 'none' }], opts)
      );
    }

    const mine = this.#running;
    await Promise.allSettled(mine.map((a) => a.finished));
    settle();
    if (this.#running !== mine) return; // closed (or reopened) mid-flight
    this.#running = [];
    if (targetImage) targetImage.style.transformOrigin = '';
    dialog.style.transition = '';
  }

  /** Images whose src/srcset were taken away so the opened one could have the connection. */
  #parked = new Set();
  #releaseTimer = null;

  /**
   * WHAT OPENING ZOOM COSTS, AND WHY IT DOES NOT HAVE TO.
   *
   * Measured on globe-pull-chain-wall-sconce, preview 160759119919, mobile 390
   * (433 CSS px), one tap on the photograph:
   *
   *   8 image requests, 544 KB, none of them from cache, and the zoomed
   *   photograph did not paint for 236ms.
   *
   * Three separate things were wrong, and only one of them was the fix of
   * 2026-09-04 (`is_main_product_media: false`, which is still right — it is
   * what stops a width=1600 derivative being fetched at high priority for a
   * dialog nobody has opened).
   *
   *   1. The dialog is a HORIZONTAL scroller of full-width slides. All eight
   *      photographs sit within Chrome's lazy-loading distance of the viewport,
   *      so `loading="lazy"` defers none of them: five fired at once, 374 KB,
   *      four of which the shopper was not looking at — competing on one
   *      connection with the one they were.
   *   2. Nothing told the browser which photograph mattered. The opened one
   *      went out at Low priority alongside the others.
   *   3. The gallery had the same picture decoded and on screen a moment
   *      earlier, and the dialog painted a hole rather than using it.
   *
   * So: everything that is not the opened photograph has its src and srcset
   * taken away before the dialog is shown, and gets them back as soon as the
   * opened one has arrived (or 900ms later, whichever is first) — early enough
   * that a swipe never reaches an empty slide, late enough that the picture
   * being looked at gets the bandwidth. Taking src away costs no layout: the
   * slides are flex children with their own box, measured unchanged at 433x937
   * with and without a src.
   *
   * @param {number} index - The index being opened.
   * @param {PointerEvent} event - The pointer event that opened it.
   */
  #prepareForOpen(index, event) {
    const { media } = this.refs;
    const target = media[index];
    if (!target) return;

    for (const [i, container] of media.entries()) {
      if (i === index) continue;
      const img = container.querySelector('img');
      if (!img) continue;
      // An image that already has a bitmap is not touched — taking its src away
      // would blank a picture that is currently correct.
      if (img.complete && img.naturalWidth > 0) continue;
      if (img.dataset.hlParked === '1') continue;
      const srcset = img.getAttribute('srcset');
      const src = img.getAttribute('src');
      if (!srcset && !src) continue;
      if (srcset) img.dataset.hlParkedSrcset = srcset;
      if (src) img.dataset.hlParkedSrc = src;
      img.dataset.hlParked = '1';
      img.removeAttribute('srcset');
      img.removeAttribute('src');
      this.#parked.add(img);
    }

    const targetImg = target.querySelector('img');
    if (targetImg) {
      targetImg.setAttribute('fetchpriority', 'high');
      targetImg.setAttribute('loading', 'eager');
      this.#coverUntilPainted(targetImg, event);
      const release = () => this.#releaseParked();
      if (targetImg.complete && targetImg.naturalWidth > 0) release();
      else if (targetImg.decode) targetImg.decode().then(release, release);
      else targetImg.addEventListener('load', release, { once: true });
    }
    clearTimeout(this.#releaseTimer);
    this.#releaseTimer = setTimeout(() => this.#releaseParked(), 900);
  }

  /**
   * The gallery slide the tap came from has this same photograph decoded and on
   * the glass. An <img> with nothing decoded paints its own background, so that
   * bitmap goes behind the dialog's copy and the shopper never sees the hole —
   * the same trick hl-variant-swap uses at the server's morph. Dropped the
   * instant the real bitmap is ready.
   *
   * @param {HTMLImageElement} img - The dialog image being opened.
   * @param {PointerEvent} event - The pointer event that opened the dialog.
   */
  #coverUntilPainted(img, event) {
    if (img.complete && img.naturalWidth > 0) return;
    const source = event?.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;
    const sourceImg = source?.querySelector('img');
    const url = sourceImg?.currentSrc;
    if (!url) return;
    const fit = getComputedStyle(img).objectFit;
    img.style.backgroundImage = `url("${url}")`;
    img.style.backgroundSize = fit === 'cover' ? 'cover' : 'contain';
    img.style.backgroundRepeat = 'no-repeat';
    img.style.backgroundPosition = 'center';
    const drop = () => {
      img.style.backgroundImage = '';
      img.style.backgroundSize = '';
      img.style.backgroundRepeat = '';
      img.style.backgroundPosition = '';
    };
    if (img.decode) img.decode().then(drop, drop);
    else img.addEventListener('load', drop, { once: true });
    setTimeout(drop, 4000);
  }

  /** Gives every parked photograph its src back. Idempotent. */
  #releaseParked() {
    clearTimeout(this.#releaseTimer);
    for (const img of this.#parked) {
      if (img.dataset.hlParkedSrcset) {
        img.setAttribute('srcset', img.dataset.hlParkedSrcset);
        delete img.dataset.hlParkedSrcset;
      }
      if (img.dataset.hlParkedSrc) {
        img.setAttribute('src', img.dataset.hlParkedSrc);
        delete img.dataset.hlParkedSrc;
      }
      delete img.dataset.hlParked;
    }
    this.#parked.clear();
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    /* "ZOOM MODE BỊ LOAD THÊM ẢNH MỘT LẦN NỮA" — it was, and for nothing.
     *
     * Shopify serves the master unchanged for any `width=` above the master's
     * own width. Measured on globe-pull-chain-wall-sconce: the dialog resolved
     * its srcset to width=1600 and got a 112 KB, 1254px file; this function
     * then fetched width=3840 and got a 112 KB, 1254px file. The same bytes,
     * the same pixels, a second time, 313ms after the first — and then it threw
     * away the <img> that had just finished decoding and put a new one in its
     * place, which is what the second flash was.
     *
     * naturalWidth smaller than the rung that produced it means the master has
     * been reached, so nothing wider exists to ask for. */
    const rung = Number((image.currentSrc.match(/[?&]width=(\d+)/) || [])[1]) || 0;
    if (rung > 0 && image.naturalWidth > 0 && image.naturalWidth < rung) return false;

    const wanted = Number((highResolutionUrl.match(/[?&]width=(\d+)/) || [])[1]) || 0;
    if (rung > 0 && wanted > 0 && wanted <= rung) return false;

    preloadImage(highResolutionUrl);

    /* The element STAYS. Replacing it discarded a decoded bitmap and left the
     * box empty for the frames between insertion and decode; assigning the URL
     * to the element that is already on screen keeps the old bitmap painted
     * until the wider one is ready to take over. */
    const probe = new Image();
    probe.decoding = 'async';
    probe.onload = () => {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.setAttribute('src', highResolutionUrl);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };
    probe.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    // The shopper is moving through the dialog; nothing may still be parked.
    this.#releaseParked();
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    /* and the strip brings it into view, as a tap on a thumbnail does (selectThumbnail);
       on desktop the selection moved while the strip stayed, so photo 16 was marked
       off-screen */
    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
    if (!this.#closing) this.#reportChange(activeIndex, Date.now() - this.#thumbAt < 1200 ? 'thumbnail' : 'swipe');
  }, 50);

  /**
   * Closes the zoom dialog: the photograph on screen flies back to the slide it
   * belongs to (media-gallery has already moved the page's slideshow there via
   * ZoomMediaSelectedEvent), the ground and rail fade with it, and only then is
   * the dialog closed.
   * @param {Event} [event]
   */
  async close(event) {
    const { dialog, media } = this.refs;
    if (!dialog.open || this.#closing) return;
    this.#closing = true;
    this.#stopRunning();

    const t = event?.target instanceof Element ? event.target : null;
    const method = event?.type === 'keydown' ? 'escape' : t?.closest('.close-button') ? 'button' : 'image';

    const current = /** @type {HTMLElement | undefined} */ (await getMostVisibleElement(media));
    const activeIndex = current ? media.indexOf(current) : this.#lastIndex;
    const closed = { index: activeIndex, method, viewed: this.#seen.size, ms: Date.now() - this.#openedAt };
    // After the flight, for the same reason as on open.
    setTimeout(() => this.#report('close', closed), ZoomDialog.#MS + 40);

    if (prefersReducedMotion() || !current) {
      this.#closing = false;
      return this.closeDialog();
    }

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );
    const slide =
      mediaGallery?.presentation === 'carousel'
        ? mediaGallery.slideshow?.slides?.[activeIndex]
        : mediaGallery?.media?.[activeIndex];

    const to = ZoomDialog.#paintedRect(slide?.querySelector('img'));
    const pic = ZoomDialog.#paintedRect(current.querySelector('img'));
    const opts = { duration: ZoomDialog.#MS, easing: ZoomDialog.#EASE, fill: /** @type {const} */ ('forwards') };

    dialog.style.transition = 'none';
    const ground = getComputedStyle(dialog).backgroundColor;
    this.#running.push(dialog.animate([{ backgroundColor: ground }, { backgroundColor: 'transparent' }], opts));
    for (const el of this.#chrome()) {
      this.#running.push(el.animate([{ opacity: 1 }, { opacity: 0 }], { ...opts, duration: ZoomDialog.#MS * 0.6 }));
    }
    if (pic && ZoomDialog.#onScreen(to)) {
      current.style.transformOrigin = '0 0';
      this.#running.push(current.animate([{ transform: 'none' }, { transform: ZoomDialog.#flight(current, pic, to) }], opts));
    } else {
      this.#running.push(current.animate([{ opacity: 1 }, { opacity: 0, transform: 'scale(0.96)' }], opts));
    }

    await Promise.allSettled(this.#running.map((a) => a.finished));
    // Close first, then drop the held end state, in the same task: the dialog is
    // gone before the photograph could snap back into place inside it.
    this.closeDialog();
    this.#stopRunning();
    current.style.transformOrigin = '';
    dialog.style.transition = '';
    this.#closing = false;
  }

  closeDialog() {
    this.#releaseParked();
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    this.#thumbAt = Date.now();
    this.#reportChange(index, 'thumbnail');
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    this.#releaseParked();
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    this.#releaseParked();
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
